import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { MarkdownEditor } from '../../molecules/MarkdownEditor';
import { FileUploader } from '../../molecules/FileUploader';
import { Modal } from '../../molecules/Modal';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const Button = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: #6c757d;
  color: white;

  &:hover:not(:disabled) {
    background-color: #5a6268;
  }
`;

const SubmitButton = styled(Button)`
  background-color: #007bff;
  color: white;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 13px;
  margin-top: 4px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 4px;
`;

const ReadOnlyField = styled.div`
  padding: 10px 12px;
  background-color: #f8f9fa;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  color: #6c757d;
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const ToggleButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  border: 2px solid ${props => props.$active ? '#007bff' : '#dee2e6'};
  border-radius: 6px;
  background-color: ${props => props.$active ? '#007bff' : 'white'};
  color: ${props => props.$active ? 'white' : '#333'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: #007bff;
    background-color: ${props => props.$active ? '#0056b3' : '#e7f3ff'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditProgramTemplateModal = ({ isOpen, onClose, onSubmit, template }) => {
  const [formData, setFormData] = useState({
    majorCategory: '',
    middleCategory: '',
    minorCategory: '',
    detailedGoal: '',
    instructionMethod: '',
    repetitionCount: 10,
    successCriteria: 9,
    behaviorType: 'positive_above',
    programSuccessCriteria: 5,
    programSuccessMode: 'consecutive',
    promptingMethod: '',
    reinforcementSchedule: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [referenceImages, setReferenceImages] = useState([]);
  const [referenceVideos, setReferenceVideos] = useState([]);
  const [useFreeForm, setUseFreeForm] = useState(false);
  const [freeFormContent, setFreeFormContent] = useState('');

  useEffect(() => {
    if (isOpen && template) {
      // Initialize form with template data
      setFormData({
        majorCategory: template.majorCategory || '',
        middleCategory: template.middleCategory || '',
        minorCategory: template.minorCategory || '',
        detailedGoal: template.detailedGoal || '',
        instructionMethod: template.instructionMethod || '',
        repetitionCount: template.repetitionCount || 10,
        successCriteria: template.successCriteria || 9,
        behaviorType: template.behaviorType || 'positive_above',
        programSuccessCriteria: template.programSuccessCriteria || 5,
        programSuccessMode: template.programSuccessMode || 'consecutive',
        promptingMethod: template.promptingMethod || '',
        reinforcementSchedule: template.reinforcementSchedule || ''
      });

      // Load existing reference files
      setReferenceImages(template.referenceFiles?.images || []);
      setReferenceVideos(template.referenceFiles?.videos || []);

      // Load markdown mode data
      setUseFreeForm(template.useFreeForm || false);
      setFreeFormContent(template.freeFormContent || '');
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        majorCategory: '',
        middleCategory: '',
        minorCategory: '',
        detailedGoal: '',
        instructionMethod: '',
        repetitionCount: 10,
        successCriteria: 9,
        behaviorType: 'positive_above',
        programSuccessCriteria: 5,
        programSuccessMode: 'consecutive',
        promptingMethod: '',
        reinforcementSchedule: ''
      });
      setErrors({});
      setReferenceImages([]);
      setReferenceVideos([]);
      setUseFreeForm(false);
      setFreeFormContent('');
    }
  }, [isOpen, template]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: name === 'repetitionCount' || name === 'successCriteria' || name === 'programSuccessCriteria'
        ? parseInt(value) || 0
        : value
    }));

    // Clear errors
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.minorCategory.trim()) {
      newErrors.minorCategory = '소주제를 입력해주세요.';
    }

    // Validate based on input mode
    if (useFreeForm) {
      if (!freeFormContent.trim()) {
        newErrors.freeFormContent = '프로그램 내용을 입력해주세요.';
      }
    } else {
      if (!formData.detailedGoal.trim()) {
        newErrors.detailedGoal = '세부 목표를 입력해주세요.';
      }
      if (!formData.instructionMethod.trim()) {
        newErrors.instructionMethod = '지시 방법을 입력해주세요.';
      }
      if (!formData.promptingMethod.trim()) {
        newErrors.promptingMethod = '촉구 방법을 입력해주세요.';
      }
      if (!formData.reinforcementSchedule.trim()) {
        newErrors.reinforcementSchedule = '강화 스케줄을 입력해주세요.';
      }
    }

    if (formData.repetitionCount < 1) {
      newErrors.repetitionCount = '반복 횟수는 1 이상이어야 합니다.';
    }
    if (formData.successCriteria < 1) {
      newErrors.successCriteria = '도달 성공 기준은 1 이상이어야 합니다.';
    }
    if (formData.successCriteria > formData.repetitionCount) {
      newErrors.successCriteria = '도달 성공 기준은 반복 횟수보다 클 수 없습니다.';
    }
    if (!formData.behaviorType) {
      newErrors.behaviorType = '행동 유형을 선택해주세요.';
    }
    if (!formData.programSuccessCriteria || formData.programSuccessCriteria < 1) {
      newErrors.programSuccessCriteria = '프로그램 성공 기준은 1 이상이어야 합니다.';
    }
    if (!formData.programSuccessMode) {
      newErrors.programSuccessMode = '성공 모드를 선택해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const templateData = {
        ...formData,
        useFreeForm,
        freeFormContent: useFreeForm ? freeFormContent : undefined,
        referenceFiles: {
          images: referenceImages,
          videos: referenceVideos
        }
      };
      await onSubmit(template.id, templateData);
      onClose();
    } catch (error) {
      console.error('Failed to update template:', error);
      setErrors({ submit: error.message || '템플릿 수정에 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  if (!template) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="프로그램 템플릿 수정">
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>대주제</Label>
          <ReadOnlyField>{formData.majorCategory}</ReadOnlyField>
          <HelpText>대주제는 수정할 수 없습니다.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>중주제</Label>
          <ReadOnlyField>{formData.middleCategory}</ReadOnlyField>
          <HelpText>중주제는 수정할 수 없습니다.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>프로그램 *</Label>
          <Input
            type="text"
            name="minorCategory"
            value={formData.minorCategory}
            onChange={handleChange}
            placeholder="예: 간단한지시따르기"
            disabled={loading}
          />
          {errors.minorCategory && <ErrorMessage>{errors.minorCategory}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>입력 방식 선택</Label>
          <ToggleButtonGroup>
            <ToggleButton
              type="button"
              $active={!useFreeForm}
              onClick={() => setUseFreeForm(false)}
              disabled={loading}
            >
              폼 입력
            </ToggleButton>
            <ToggleButton
              type="button"
              $active={useFreeForm}
              onClick={() => setUseFreeForm(true)}
              disabled={loading}
            >
              줄글로 입력
            </ToggleButton>
          </ToggleButtonGroup>
          <HelpText>
            폼 입력: 각 항목을 개별적으로 입력 | 줄글로 입력: 자유 형식으로 작성
          </HelpText>
        </FormGroup>

        {!useFreeForm ? (
          <>
            <FormGroup>
              <Label>세부 목표 *</Label>
              <Textarea
                name="detailedGoal"
                value={formData.detailedGoal}
                onChange={handleChange}
                placeholder='예: "앉아", "일어나", "이리와" 등 간단한 한 단계 지시를 따를 수 있다'
                disabled={loading}
              />
              {errors.detailedGoal && <ErrorMessage>{errors.detailedGoal}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>지시 방법 *</Label>
              <Textarea
                name="instructionMethod"
                value={formData.instructionMethod}
                onChange={handleChange}
                placeholder="예: 명확하고 간결한 언어로 지시하고 즉각적인 반응을 유도한다"
                disabled={loading}
              />
              {errors.instructionMethod && <ErrorMessage>{errors.instructionMethod}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>촉구 방법 *</Label>
              <Input
                type="text"
                name="promptingMethod"
                value={formData.promptingMethod}
                onChange={handleChange}
                placeholder="예: 신체적 유도 → 제스처 → 독립"
                disabled={loading}
              />
              {errors.promptingMethod && <ErrorMessage>{errors.promptingMethod}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>강화 스케줄 *</Label>
              <Input
                type="text"
                name="reinforcementSchedule"
                value={formData.reinforcementSchedule}
                onChange={handleChange}
                placeholder="예: FR-2 (2회 성공 시 강화)"
                disabled={loading}
              />
              {errors.reinforcementSchedule && <ErrorMessage>{errors.reinforcementSchedule}</ErrorMessage>}
            </FormGroup>
          </>
        ) : (
          <FormGroup>
            <Label>프로그램 내용 *</Label>
            <MarkdownEditor
              value={freeFormContent}
              onChange={setFreeFormContent}
              height={400}
              placeholder='세부목표, 지시방법, 촉구방법, 강화스케줄 등을 작성하세요...\n\n예시:\n# 세부 목표\n- 항목 1\n- 항목 2\n\n# 지시 방법\n내용...'
              folder="program-template-images"
            />
            {errors.freeFormContent && <ErrorMessage>{errors.freeFormContent}</ErrorMessage>}
          </FormGroup>
        )}

        <FormGroup>
          <Label>반복 횟수 *</Label>
          <Input
            type="number"
            name="repetitionCount"
            value={formData.repetitionCount}
            onChange={handleChange}
            min="1"
            disabled={loading}
          />
          {errors.repetitionCount && <ErrorMessage>{errors.repetitionCount}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>도달 성공 기준 *</Label>
          <Input
            type="number"
            name="successCriteria"
            value={formData.successCriteria}
            onChange={handleChange}
            min="1"
            disabled={loading}
          />
          {errors.successCriteria && <ErrorMessage>{errors.successCriteria}</ErrorMessage>}
          <HelpText>반복 횟수 중 몇 회를 성공해야 목표 달성으로 간주할지 입력하세요.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>행동 유형 *</Label>
          <Select
            name="behaviorType"
            value={formData.behaviorType}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="positive_above">긍정행동 (성공 횟수 ≥ 성공기준)</option>
            <option value="perform_only">수행만 하면 성공 (수행 여부만 기록)</option>
          </Select>
          {errors.behaviorType && <ErrorMessage>{errors.behaviorType}</ErrorMessage>}
          <HelpText>긍정행동은 성공 횟수를 측정하고, 수행만 하면 성공은 수행 여부만 기록합니다.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>프로그램 성공 기준 *</Label>
          <Input
            type="number"
            name="programSuccessCriteria"
            value={formData.programSuccessCriteria}
            onChange={handleChange}
            min="1"
            disabled={loading}
            placeholder="예: 5"
          />
          {errors.programSuccessCriteria && <ErrorMessage>{errors.programSuccessCriteria}</ErrorMessage>}
          <HelpText>몇 회의 세션을 성공해야 프로그램이 완료되는지 입력하세요.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>성공 모드 *</Label>
          <Select
            name="programSuccessMode"
            value={formData.programSuccessMode}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="consecutive">연속 (연속으로 N회 성공)</option>
            <option value="cumulative">누적 (총 N회 성공)</option>
          </Select>
          {errors.programSuccessMode && <ErrorMessage>{errors.programSuccessMode}</ErrorMessage>}
          <HelpText>연속 모드는 연속된 세션 성공을, 누적 모드는 전체 성공 세션 수를 계산합니다.</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>참고자료 (선택)</Label>
          <FileUploader
            images={referenceImages}
            videos={referenceVideos}
            onImagesChange={setReferenceImages}
            onVideosChange={setReferenceVideos}
          />
          <HelpText>프로그램 관련 이미지(최대 5개)와 동영상(최대 3개, 각 50MB 이하)을 추가할 수 있습니다.</HelpText>
        </FormGroup>

        {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}

        <ButtonGroup>
          <CancelButton type="button" onClick={onClose} disabled={loading}>
            취소
          </CancelButton>
          <SubmitButton type="submit" disabled={loading}>
            {loading ? '수정 중...' : '템플릿 수정'}
          </SubmitButton>
        </ButtonGroup>
      </Form>
    </Modal>
  );
};

export default EditProgramTemplateModal;
